/**
 * Response DTOs.
 *
 * docs/SECURITY.md §2 defence 1: the end-user view and the staff view are
 * DISJOINT TYPES, never one object plus a "you're staff, so read this field"
 * boolean. A controller wired to the wrong shape is a compile error rather
 * than a runtime leak. This file is what makes that possible — every export
 * here is a schema a handler either returns whole or doesn't touch.
 *
 * The small domain enums below (source, confirmation status, visibility,
 * author kind, action source, verdict kind, SLA state) mirror the Postgres
 * enums of the same name in packages/db/prisma/schema.prisma byte-for-byte.
 * There is no automatic drift check for them the way ticket-state-machine.ts
 * has `diffAgainstDatabase()` for transitions — keep them in sync by hand if
 * the schema ever changes.
 */
import { z } from 'zod';
import { INTENTS, TICKET_STATUSES } from './ticket-state-machine.js';
import { emailCreationUserRecordSchema } from './email-creation.js';
import { emailDeletionUserRecordSchema } from './email-deletion.js';
// ---------------------------------------------------------------------------
// Shared domain enums (wire vocabulary, not the Prisma client)
// ---------------------------------------------------------------------------
export const ticketStatusSchema = z.enum(TICKET_STATUSES);
export const intentSchema = z.enum(INTENTS);
export const TICKET_SOURCES = ['WEB', 'EMAIL', 'API', 'PHONE', 'IMPORT'];
export const ticketSourceSchema = z.enum(TICKET_SOURCES);
export const USER_CONFIRMATION_STATUSES = [
    'PENDING',
    'CONFIRMED',
    'REJECTED',
    'AUTO_CONFIRMED',
    'ADMIN_OVERRIDE',
];
export const userConfirmationStatusSchema = z.enum(USER_CONFIRMATION_STATUSES);
export const SLA_STATES = ['ON_TRACK', 'AT_RISK', 'BREACHED', 'PAUSED', 'MET', 'NOT_APPLICABLE'];
export const slaStateSchema = z.enum(SLA_STATES);
export const COMMENT_VISIBILITIES = ['PUBLIC', 'INTERNAL'];
export const commentVisibilitySchema = z.enum(COMMENT_VISIBILITIES);
export const AUTHOR_KINDS = ['USER', 'TECHNICIAN', 'SYSTEM'];
export const authorKindSchema = z.enum(AUTHOR_KINDS);
export const ACTION_SOURCES = ['WEB', 'EMAIL_LINK', 'API', 'AUTO', 'ADMIN'];
export const actionSourceSchema = z.enum(ACTION_SOURCES);
export const VERDICT_KINDS = ['CONFIRMED', 'REJECTED'];
export const verdictKindSchema = z.enum(VERDICT_KINDS);
// ---------------------------------------------------------------------------
// Small reusable reference shapes
// ---------------------------------------------------------------------------
const idNameRefSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
});
const priorityRefSchema = z.object({
    id: z.string().uuid(),
    code: z.string(),
    name: z.string(),
    rank: z.number().int(),
});
const userRefSchema = z.object({
    id: z.string().uuid(),
    displayName: z.string(),
});
// ---------------------------------------------------------------------------
// Tickets
// ---------------------------------------------------------------------------
export const ticketListItemDtoSchema = z.object({
    id: z.string().uuid(),
    ticketNumber: z.string(),
    title: z.string(),
    status: ticketStatusSchema,
    userConfirmationStatus: userConfirmationStatusSchema,
    priority: priorityRefSchema.nullable(),
    requester: userRefSchema.nullable(),
    assignedTeam: idNameRefSchema.nullable(),
    assignedTechnician: userRefSchema.nullable(),
    /** Co-assigned technicians besides the lead (`ticket_participants` role ASSIGNEE). Absent = none. */
    coAssignees: z.array(userRefSchema).optional(),
    /** Category/subcategory names help a queue row scan ("Network › VPN"); ids and names only. */
    category: idNameRefSchema.nullable(),
    subcategory: idNameRefSchema.nullable(),
    source: ticketSourceSchema,
    createdAt: z.date(),
    updatedAt: z.date(),
    resolvedAt: z.date().nullable(),
    slaState: slaStateSchema,
    resolutionDueAt: z.date().nullable(),
    reopenCount: z.number().int().nonnegative(),
    version: z.number().int().nonnegative(),
});
/**
 * Everything in the list item, plus the full lifecycle timeline and the
 * fields only the detail page needs. `availableIntents` is what drives which
 * action buttons the UI offers — it is derived server-side from
 * `intentsFrom(ticket.status)` filtered by the caller's permissions, never
 * guessed by the client from `status` alone.
 */
export const ticketDetailDtoSchema = ticketListItemDtoSchema.extend({
    description: z.string(),
    category: idNameRefSchema.nullable(),
    subcategory: idNameRefSchema.nullable(),
    impact: idNameRefSchema.nullable(),
    urgency: idNameRefSchema.nullable(),
    severity: idNameRefSchema.nullable(),
    // Independent lifecycle timestamps — never reconstructed from `status`.
    triagedAt: z.date().nullable(),
    assignedAt: z.date().nullable(),
    firstResponseAt: z.date().nullable(),
    workStartedAt: z.date().nullable(),
    pendingSince: z.date().nullable(),
    resolverConfirmedAt: z.date().nullable(),
    userConfirmationAt: z.date().nullable(),
    closedAt: z.date().nullable(),
    lastReopenedAt: z.date().nullable(),
    currentResolutionId: z.string().uuid().nullable(),
    unverifiedSender: z.boolean(),
    // SLA (project brief §20) — the RESOLUTION target's due date and state are
    // already on `ticketListItemDtoSchema` above (queue columns need them
    // too); these two are detail-only. `slaPausedMs` is the accumulated
    // PHYSICAL (not business) time the resolution clock has spent paused
    // across every PENDING_USER/PENDING_EXTERNAL interval and every
    // RESOLVED/CLOSED interval this ticket has been through — "how long has
    // this ticket actually been waiting on someone", for the sidebar.
    firstResponseDueAt: z.date().nullable(),
    slaPausedMs: z.number().nonnegative(),
    availableIntents: z.array(intentSchema),
    /** Email Creation tickets only: the requested employees, in the order submitted. */
    emailCreationUsers: z.array(emailCreationUserRecordSchema).optional(),
    emailDeletionUsers: z.array(emailDeletionUserRecordSchema).optional(),
});
// ---------------------------------------------------------------------------
// Resolutions and verdicts
// ---------------------------------------------------------------------------
export const resolutionVerdictDtoSchema = z.object({
    id: z.string().uuid(),
    resolutionId: z.string().uuid(),
    verdict: verdictKindSchema,
    actorId: z.string().uuid(),
    comment: z.string().nullable(),
    source: actionSourceSchema,
    /** False for a verdict on a superseded attempt — see docs/API.md §4. */
    isEffective: z.boolean(),
    createdAt: z.date(),
});
export const resolutionAttemptDtoSchema = z.object({
    id: z.string().uuid(),
    attemptNo: z.number().int().positive(),
    resolverId: z.string().uuid(),
    /**
     * Nullable, like every other person-ref in this file (`requester`,
     * `assignedTechnician`, `authorDisplayName`): the resolver account can be
     * disabled or otherwise gone by the time this attempt is read back, and
     * `ticket.mapper.ts#toResolutionAttempt` reflects that with `?? null`.
     */
    resolverDisplayName: z.string().nullable(),
    problemSummary: z.string(),
    rootCause: z.string().nullable(),
    actionTaken: z.string(),
    wasTested: z.boolean(),
    testResult: z.string().nullable(),
    followUpRequired: z.boolean(),
    followUpNotes: z.string().nullable(),
    resolutionCategory: idNameRefSchema,
    /** The explicit attestation. A resolution cannot exist without it. */
    resolverConfirmedAt: z.date(),
    resolverConfirmationText: z.string(),
    createdAt: z.date(),
    /** The requester's verdict on THIS attempt, if the current one is this one. */
    effectiveVerdict: resolutionVerdictDtoSchema.nullable(),
});
// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------
/**
 * One shape for both public and internal comments. Access control (which
 * rows a caller ever sees) happens in the query — docs/SECURITY.md §2 defence
 * 2/3: `GET /tickets/:id/comments` is visibility-filtered in the query, not
 * after, and RLS on `ticket_comments` is the backstop if it isn't. This type
 * describes one already-authorized row, not a filter.
 */
export const commentDtoSchema = z.object({
    id: z.string().uuid(),
    authorId: z.string().uuid().nullable(),
    authorDisplayName: z.string().nullable(),
    authorKind: authorKindSchema,
    visibility: commentVisibilitySchema,
    body: z.string(),
    bodyFormat: z.string(),
    source: actionSourceSchema,
    resolutionId: z.string().uuid().nullable(),
    /** True when quote-stripping produced near-empty output — docs/EMAIL.md §8. */
    rawFallback: z.boolean(),
    createdAt: z.date(),
    editedAt: z.date().nullable(),
});
// ---------------------------------------------------------------------------
// Public confirmation landing page
// ---------------------------------------------------------------------------
/**
 * The minimal projection for the unauthenticated confirmation landing page
 * (docs/SECURITY.md §4, docs/API.md §6). This has NO field capable of
 * carrying a ticket description, resolution notes or comment bodies — that
 * is not a convention, it is the entire point of the type existing
 * separately from `TicketDetailDto`. Anyone holding the bearer link — a
 * forwarded thread, a shared workstation's browser history, a link-rewriting
 * mail gateway's own logs — sees no clinical content, ever. `title` is
 * gated behind a setting and defaults off. `.strict()` below means an extra
 * field accidentally merged onto this shape fails validation instead of
 * silently shipping.
 */
export const publicTicketViewDtoSchema = z
    .object({
    ticketNumber: z.string(),
    resolverDisplayName: z.string(),
    resolvedAt: z.date(),
    title: z.string().optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Dashboard counts
// ---------------------------------------------------------------------------
export const ticketCountsDtoSchema = z.object({
    open: z.number().int().nonnegative(),
    assignedToMe: z.number().int().nonnegative(),
    unassigned: z.number().int().nonnegative(),
    awaitingConfirmation: z.number().int().nonnegative(),
    resolved: z.number().int().nonnegative(),
    reopened: z.number().int().nonnegative(),
    slaAtRisk: z.number().int().nonnegative(),
    slaBreached: z.number().int().nonnegative(),
    /**
     * Visible tickets per lifecycle status, same visibility scope as every
     * other count here (drives the dashboard's status tabs). Every status is
     * present, zero-filled, so a client never has to guess at a missing key.
     */
    byStatus: z.record(z.enum(TICKET_STATUSES), z.number().int().nonnegative()),
});
export function paginatedDtoSchema(itemSchema) {
    return z.object({
        data: z.array(itemSchema),
        nextCursor: z.string().nullable(),
        hasMore: z.boolean(),
        totalCount: z.number().int().nonnegative().optional(),
    });
}
// ---------------------------------------------------------------------------
// Audit trail — GET /tickets/:id/timeline and GET /tickets/:id/audit
//
// docs/SECURITY.md §2 defence 3: these are deliberately different shapes
// reading the same underlying `ticket_audit_log` table, not one shape with a
// staff/end-user branch.
//
//   - `/timeline` (`TimelineEventDto`) is the curated, end-user-safe
//     projection: a fixed whitelist of event types, no `old_value`/
//     `new_value` blob, and — per docs/SECURITY.md §2 defence 4 — an
//     internal-only comment's creation event is excluded outright rather
//     than included with its body stripped, so a requester cannot even infer
//     that an internal note exists.
//   - `/audit` (`AuditLogEntryDto`) is the raw feed: every event type, with
//     `oldValue`/`newValue`, gated on `ticket:audit:read:any` (ADMIN,
//     AUDITOR only). It never carries a comment BODY either — the trigger
//     and the application code that write these rows only ever put a
//     comment's id and visibility in `newValue`, never its text — but it is
//     not filtered by event type or visibility the way the timeline is.
// ---------------------------------------------------------------------------
/** The event types `/timeline` will ever show. Every other row is dropped. */
export const TIMELINE_EVENT_TYPES = [
    'TICKET_CREATED',
    'TICKET_STATUS_CHANGED',
    'TICKET_ASSIGNMENT_CHANGED',
    'TICKET_PRIORITY_CHANGED',
    'TICKET_COMMENT_CREATED',
    'TICKET_ATTACHMENT_UPLOADED',
    'TICKET_ATTACHMENT_DELETED',
];
export const timelineEventDtoSchema = z.object({
    /** `ticket_audit_log.id` (bigint) as a decimal string — also the page cursor. */
    id: z.string(),
    eventType: z.enum(TIMELINE_EVENT_TYPES),
    occurredAt: z.date(),
    actorRole: z.string().nullable(),
    /** Populated only for `TICKET_STATUS_CHANGED`; null otherwise. */
    fromStatus: ticketStatusSchema.nullable(),
    toStatus: ticketStatusSchema.nullable(),
    /**
     * Display name of the authenticated actor recorded server-side on the audit
     * row (never client-supplied). Null for system/automation events.
     */
    actorName: z.string().nullable(),
    /**
     * Populated only for `TICKET_ASSIGNMENT_CHANGED`; null otherwise. Names are
     * resolved server-side from the audit row's old/new values.
     */
    assignment: z
        .object({
        assigneeName: z.string().nullable(),
        teamName: z.string().nullable(),
        previousAssigneeName: z.string().nullable(),
    })
        .nullable(),
});
export const auditLogEntryDtoSchema = z.object({
    id: z.string(),
    ticketId: z.string().uuid().nullable(),
    eventType: z.string(),
    actorId: z.string().uuid().nullable(),
    actorRole: z.string().nullable(),
    actorIp: z.string().nullable(),
    source: actionSourceSchema,
    oldValue: z.unknown().nullable(),
    newValue: z.unknown().nullable(),
    metadata: z.unknown().nullable(),
    reason: z.string().nullable(),
    createdAt: z.date(),
});
//# sourceMappingURL=ticket-dto.js.map